import classNames from 'classnames'
import React from 'react'

import { Accordion, AccordionItem } from '../../accordion/accordion.js'
import { GridColumn } from '../../grid/grid-column.js'
import { Grid } from '../../grid/grid.js'
import { CheckSmallIcon } from '../../icon/icon.js'
import { List, ListItem, ListItemProps } from '../../list/list.js'

export interface CartGridProps {
    /**
     * If true, the grid is collapsible.
     */
    collapsible?: boolean
    /**
     * Array of rows.
     */
    rows: CartGridRowProps[]
    /**
     * If true, this item will not have bottom border
     */
    hideSeparator?: boolean
}

export interface CartGridRowProps {
    /**
     * Left column (mandatory).
     * Mostly used for title.
     * Has maximum width.
     */
    left: CartGridLeftCellProps
    /**
     * Right column (optional).
     * Mostly used for price.
     * Has minimum width.
     */
    right?: CartGridRightCellProps
}

export interface CartGridLeftCellProps {
    /**
     * Text is required!
     */
    text: string | React.ReactNode
    /**
     * Text size.
     * Small by default!
     */
    size?: 'tiny' | 'small' | 'huge' | 'regular'
    /**
     * Text weight.
     * Bold by default!
     */
    weight?: 'bold' | 'regular'
    /**
     * Optional list.
     */
    list?: ListItemProps[]
}

export interface CartGridRightCellProps {
    /**
     * Text is required!
     */
    text: string | React.ReactNode
    /**
     * Optional description on separate row.
     * Font size is always small!
     */
    description?: string
    /**
     * If true text is blue.
     */
    isBlue?: boolean
    /**
     * Text size.
     * Regular by default!
     */
    size?: 'tiny' | 'small' | 'huge' | 'regular'
    /**
     * Text weight.
     * Bold by default!
     */
    weight?: 'bold' | 'regular'
}

export const CartGrid = (props: CartGridProps): JSX.Element => {
    const renderLeftColumn = (left: CartGridLeftCellProps) => {
        const { list, text, size = 'small', weight = 'bold' } = left

        const className: string = classNames('cart__title', {
            [`text-${size}`]: size,
            bold: weight === 'bold',
        })

        return (
            <GridColumn width={['max-xs']} className="h-text-left">
                <span className={className}>{text}</span>
                {list && (
                    <List type="icon">
                        {list.map((listItem: ListItemProps, index: number) => {
                            return (
                                <ListItem className="text-small" icon={CheckSmallIcon} key={index}>
                                    {listItem.children}
                                </ListItem>
                            )
                        })}
                    </List>
                )}
            </GridColumn>
        )
    }

    const renderRightColumn = (right: CartGridRightCellProps) => {
        const { description, isBlue, text, size, weight = 'bold' } = right

        const className: string = classNames('cart__price', isBlue && 'text-price', {
            [`text-${size}`]: size,
            bold: weight === 'bold',
        })

        return (
            <GridColumn width={['min-xs']} className="h-text-right">
                <p className={className}>{text}</p>
                {description && <p className="cart__description text-small">{description}</p>}
            </GridColumn>
        )
    }

    const renderRows = (rows: CartGridRowProps[]) =>
        rows.map((row: CartGridRowProps, index: number) => {
            return (
                <Grid className="cart__grid" noWrap={['xs']} key={index}>
                    {row.left && renderLeftColumn(row.left)}
                    {row.right && renderRightColumn(row.right)}
                </Grid>
            )
        })

    const renderCollapsibleRows = () => {
        const firstRow: CartGridRowProps[] = [props.rows[0]].filter(Boolean)
        const otherRows: CartGridRowProps[] = props.rows.slice(1).filter(Boolean)

        if (!firstRow.length && !otherRows.length) {
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return <></>
        }

        return (
            <Accordion className="accordion--variant-cart">
                <AccordionItem id="cart-grid-accordion" label={renderRows(firstRow)} smallIcon={true}>
                    {renderRows(otherRows)}
                </AccordionItem>
            </Accordion>
        )
    }

    return props.collapsible ? renderCollapsibleRows() : <>{renderRows(props.rows)}</>
}
